<template>
  <div>
    <v-dialog
      v-model="dialogOpen"
      persistent
      max-width="560px"
    >
      <v-card class="swipe-details-card rounded-lg overflow-hidden">
        <v-toolbar dense flat class="elevation-0 gradient-bg white--text">
          <v-toolbar-title class="text-subtitle-1 font-weight-medium">
            Swipe Settings
          </v-toolbar-title>
          <v-spacer />
          <v-btn @click="close_dialog" icon dark aria-label="Close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="px-0 pb-0 pt-2">
          <div class="px-4 pb-3 swipe-details-intro">
            <div class="text-h6 font-weight-medium text--primary">
              {{ memberName }}
            </div>
            <div
              class="text-body-2 text--secondary text-truncate"
              :title="organisationName"
            >
              {{ organisationName }}
            </div>
            <div v-if="employeeId" class="text-caption text--secondary mt-1">
              Member ID: {{ employeeId }}
            </div>
          </div>

          <v-divider class="mx-4" />

          <v-list class="py-2 swipe-details-list swipe-details-list--bordered" dense>
            <v-list-item
              v-for="row in featureRows"
              :key="row.key"
              class="px-4 swipe-details-row"
            >
              <div class="swipe-details-row__inner">
                <v-icon
                  class="swipe-details-help-icon flex-shrink-0"
                  color="primary"
                  size="22"
                >
                  mdi-comment-question-outline
                </v-icon>
                <div
                  class="swipe-details-switch flex-shrink-0"
                  :class="{ 'swipe-details-switch--on': row.on }"
                  aria-hidden="true"
                >
                  <span class="swipe-details-switch__thumb"></span>
                </div>
                <div class="swipe-details-label pl-2">
                  <span class="swipe-details-label__name">{{ row.label }}</span>
                  <span class="swipe-details-label__sep"> : </span>
                  <span
                    class="swipe-details-label__value"
                    :class="
                      row.on
                        ? 'swipe-details-label__value--yes'
                        : 'swipe-details-label__value--no'
                    "
                  >
                    {{ row.on ? "Yes" : "No" }}
                  </span>
                </div>
              </div>
            </v-list-item>
          </v-list>


          <div class="px-4 py-4 location-scan-block">
            <div
              class="text-subtitle-2 font-weight-medium text--primary mb-3"
            >
              Where this member can scan
            </div>

            <div
              v-for="opt in locationScanRows"
              :key="opt.value"
              class="location-scan-row d-flex align-start"
            >
              <div
                class="location-scan-row__lead flex-shrink-0 d-flex justify-center"
              >
                <v-icon
                  v-if="opt.showHelpIcon"
                  color="primary"
                  size="22"
                  class="location-scan-row__help"
                >
                  mdi-comment-question-outline
                </v-icon>
              </div>
              <v-icon
                class="location-scan-row__radio-icon flex-shrink-0"
                :color="locationScanMode === opt.value ? 'primary' : undefined"
                :class="{
                  'location-scan-row__radio-icon--off':
                    locationScanMode !== opt.value,
                }"
                size="26"
              >
                {{
                  locationScanMode === opt.value
                    ? "mdi-radiobox-marked"
                    : "mdi-radiobox-blank"
                }}
              </v-icon>
              <div
                class="location-scan-row__text pl-2 pt-1"
                v-text="opt.text"
              ></div>
            </div>
          </div>
        </v-card-text>

        <v-divider />
        <v-card-actions class="px-4 py-3 grey lighten-5">
          <span class="text-caption text--secondary">
            Display only — these values come from the member profile.
          </span>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  props: {
    viewSwipeDetailsDialog: Boolean,
    swipeDetails: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    dialogOpen: {
      get() {
        return this.viewSwipeDetailsDialog;
      },
      set(val) {
        if (!val) this.close_dialog();
      },
    },
    d() {
      return this.swipeDetails && typeof this.swipeDetails === "object"
        ? this.swipeDetails
        : {};
    },
    memberName() {
      return this.d.user_name && this.d.user_name !== "N/A"
        ? this.d.user_name
        : "Member";
    },
    organisationName() {
      return this.d.organisation_name || "—";
    },
    employeeId() {
      const id = this.d.employee_id;
      if (id === undefined || id === null || id === "" || id === "N/A") {
        return "";
      }
      return String(id);
    },
    liveLocationOn() {
      const locOk = this.locationIdImpliesTracking(this.d.location_id);
      const locFlag = this.d.is_location_enabled;
      if (locFlag === undefined || locFlag === null) {
        return locOk;
      }
      return this.bool(locFlag) && locOk;
    },
    formMandatoryOn() {
      const v =
        this.d.is_form_mandatory ??
        this.d.is_mandatory_form ??
        this.d.form_mandatory;
      return this.bool(v);
    },
    /** Which location rule applies: scan_anywhere | any_org_locations | single_location | null */
    locationScanMode() {
      if (this.bool(this.d.is_scan_any_where)) {
        return "scan_anywhere";
      }
      const raw = this.d.location_id;
      if (raw === undefined || raw === null) {
        return null;
      }
      const s = String(raw).trim();
      if (!s) return null;
      const upper = s.toUpperCase();
      const off = new Set([
        "NONE",
        "NO_LOCATION",
        "DISABLED",
        "OFF",
        "NULL",
        "UNDEFINED",
      ]);
      if (off.has(upper)) return null;
      if (upper === "ANY_LOCATIONS" || upper === "ALL") {
        return "any_org_locations";
      }
      if (this.isUuidLike(s)) {
        return "single_location";
      }
      return null;
    },
    scanAnywhereLabel() {
      const yn = this.bool(this.d.is_scan_any_where) ? "Yes" : "No";
      return `Scan Anywhere (outside the organisation's geofenced locations) : ${yn}`;
    },
    locationScanRows() {
      return [
        {
          value: "scan_anywhere",
          showHelpIcon: true,
          text: this.scanAnywhereLabel,
        },
        {
          value: "any_org_locations",
          showHelpIcon: false,
          text:
            "Scan within organisation locations (any geo-fenced locations)",
        },
        {
          value: "single_location",
          showHelpIcon: false,
          text: "Assign only one location to user (choose the location)",
        },
      ];
    },
    featureRows() {
      return [
        {
          key: "qr",
          label: "QR Scan",
          on: this.bool(this.d.is_qr_enabled),
        },
        {
          key: "face",
          label: "Face",
          on: this.bool(this.d.is_face_enabled),
        },
        {
          key: "beacon",
          label: "Beacon",
          on: this.bool(this.d.is_beacon_enabled),
        },
        {
          key: "proxy",
          label: "Proxy",
          on: this.bool(this.d.is_proxy_enabled),
        },
        {
          key: "liveLoc",
          label: "Live Location Tracking",
          on: this.liveLocationOn,
        },
        {
          key: "formMandatory",
          label: "Form Mandatory",
          on: this.formMandatoryOn,
        },
      ];
    },
  },
  methods: {
    close_dialog() {
      this.$emit("close_dialog", 0);
    },
    bool(v) {
      if (v === true || v === 1 || v === "1" || v === "true") return true;
      if (v === false || v === 0 || v === "0" || v === "false") return false;
      return Boolean(v);
    },
    locationIdImpliesTracking(locationId) {
      if (locationId === undefined || locationId === null) return false;
      const s = String(locationId).trim();
      if (!s) return false;
      const upper = s.toUpperCase();
      const off = new Set([
        "NONE",
        "NO_LOCATION",
        "DISABLED",
        "OFF",
        "NULL",
        "UNDEFINED",
      ]);
      if (off.has(upper)) return false;
      if (upper === "ANY_LOCATIONS" || upper === "ALL") return true;
      return this.isUuidLike(s);
    },
    isUuidLike(s) {
      return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        String(s).trim()
      );
    },
  },
};
</script>

<style scoped>
.swipe-details-card {
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.swipe-details-intro {
  min-height: 48px;
}

.swipe-details-list {
  background: transparent !important;
}

.swipe-details-row {
  min-height: 52px !important;
  align-items: center;
}

.swipe-details-row__inner {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 4px;
}

.swipe-details-help-icon {
  opacity: 0.95;
}

/* Read-only switch (display only) */
.swipe-details-switch {
  width: 38px;
  height: 22px;
  border-radius: 11px;
  background: #e8e8e8;
  position: relative;
  flex-shrink: 0;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.06);
  transition: background 0.2s ease;
}

.swipe-details-switch--on {
  background: rgba(244, 129, 63, 0.38);
}

.swipe-details-switch__thumb {
  position: absolute;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  top: 2px;
  left: 2px;
  background: #bdbdbd;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
  transition: transform 0.2s ease, background 0.2s ease;
}

.swipe-details-switch--on .swipe-details-switch__thumb {
  transform: translateX(16px);
  background: #f4813f;
}

.swipe-details-label {
  flex: 1;
  min-width: 0;
  font-size: 0.95rem;
  line-height: 1.35;
}

.swipe-details-label__name {
  color: rgba(0, 0, 0, 0.72);
  font-weight: 500;
}

.swipe-details-label__sep {
  color: rgba(0, 0, 0, 0.38);
}

.swipe-details-label__value--yes {
  color: rgba(0, 0, 0, 0.87);
  font-weight: 600;
}

.swipe-details-label__value--no {
  color: rgba(0, 0, 0, 0.38);
  font-weight: 500;
}

.swipe-details-list--bordered {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  margin-bottom: 4px;
}

.location-scan-block {
  background: rgba(0, 0, 0, 0.02);
}

.location-scan-row {
  padding: 10px 0;
  min-height: 48px;
}

.location-scan-row__lead {
  width: 28px;
  margin-right: 6px;
}

.location-scan-row__help {
  opacity: 0.95;
}

.location-scan-row__radio-icon--off {
  color: #b0b0b0 !important;
}

.location-scan-row__text {
  font-size: 0.9rem;
  line-height: 1.45;
  color: rgba(0, 0, 0, 0.78);
}
</style>
